import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Theme,
  useTheme,
} from "@mui/material";
import Loader from "components/Loader";
import RedirectBackButton from "components/RedirectBackButton";
import RowElement from "components/RowElement";
import { format } from "date-fns";
import useGlobalContext from "hooks/globalData";
import useTitle from "hooks/useTitle";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { useParams } from "react-router-dom";
import { Email } from "services/KnowfullyAdminClient";
import EmailProviderIcon from "./EmailProviderIcon";

const useStyles = (theme: Theme) => ({
  emailInfo: {
    marginBottom: theme.spacing(4),
    marginTop: theme.spacing(2),
  },
  previewPaper: {
    paddingBottom: theme.spacing(2),
  },
  previewHeader: {
    pl: theme.spacing(2),
    pt: theme.spacing(1),
    "& h2": {
      margin: 0,
    },
  },
  preview: {
    border: "1px solid",
    borderColor: theme.palette.secondary.main,
    marginTop: 0,
    marginLeft: theme.spacing(20),
    marginRight: theme.spacing(20),
    marginBottom: theme.spacing(2),
    padding: theme.spacing(1),
  },
});

const EmailDetails = () => {
  const styles = useStyles(useTheme());
  const { formatMessage: t } = useIntl();
  const { emailsUrl, emailService } = useGlobalContext();

  const { emailId } = useParams<{
    emailId: string;
  }>();

  const emailResult = useQuery<Email>(
    ["email", emailId],
    () => emailService.getEmail(emailId!),
    {
      enabled: !!emailId,
    }
  );

  const email = emailResult?.data;
  useTitle(
    t({ id: "sentEmailTitle" }, { name: email?.toEmails || t({ id: "user" }) })
  );

  const formatDateTime = (date: Date | undefined) => {
    if (!date) return "";
    return format(date, "MM/dd/yyyy HH:mm:ss");
  };

  const rows = [
    RowElement(
      "sentLabel",
      formatDateTime(email?.createdOn),
      emailResult.isLoading
    ),
    RowElement("subject", email?.subject ?? "", emailResult.isLoading),
    RowElement("senderLabel", email?.senderEmail ?? "", emailResult.isLoading),
    RowElement("recipientLabel", email?.toEmails ?? "", emailResult.isLoading),
    RowElement(
      "organization",
      email?.organizationName ?? "",
      emailResult.isLoading
    ),
    RowElement(
      "templateLabel",
      email?.emailTemplateName ?? "",
      emailResult.isLoading
    ),
    RowElement(
      "provider",
      email?.emailProvider ?? "",
      emailResult.isLoading,
      <EmailProviderIcon provider={email?.emailProvider} showName />
    ),
  ].filter((r) => !!r.component);

  if (emailResult.isLoading) {
    return <Loader />;
  }

  return (
    <>
      {!emailResult.isFetching && (
        <>
          <RedirectBackButton
            redirectRoute={emailsUrl()}
            labelKey="backToListButtonLabel"
          />

          <Box sx={styles.emailInfo}>
            <TableContainer component={Paper}>
              <Table>
                <TableBody>
                  {rows.map(({ title, component }) => (
                    <TableRow key={title}>
                      <TableCell component="th" scope="row">
                        {title}
                      </TableCell>
                      <TableCell>{component}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>

          <Paper sx={styles.previewPaper}>
            <Box sx={styles.previewHeader}>
              <h2>{t({ id: "email" })}</h2>
            </Box>
            <Box sx={styles.preview}>
              <div dangerouslySetInnerHTML={{ __html: email?.content ?? "" }} />
            </Box>
          </Paper>
        </>
      )}
    </>
  );
};

export default EmailDetails;
